import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import { useSelector } from "react-redux";

import useCurrentEnvironment from "@/hooks/use-environment";
import type { FreshpaintState } from "@/reducers";

export type SettingsEnvironment = {
  ID: string;
  name: string;
  project: { ID: string | null; name: string };
};

type SettingsEnvironmentContextValue = {
  selectedEnvironment: SettingsEnvironment | null;
  selectedEnvID: string | null;
  setSelectedEnvID: (envID: string) => void;
};

const SettingsEnvironmentContext =
  createContext<SettingsEnvironmentContextValue | null>(null);

/**
 * Scoped environment selection for the settings section. Unlike the main nav's
 * picker, selecting here does NOT switch the app's global environment (no redux
 * dispatch, no query invalidation) — it only changes which project/environment
 * the settings pages read and write. Returns null outside the settings layout.
 */
export const useSettingsEnvironment = () =>
  useContext(SettingsEnvironmentContext);

export const SettingsEnvironmentProvider = ({
  children,
}: PropsWithChildren<unknown>) => {
  const globalEnvironment = useCurrentEnvironment();
  const projects = useSelector(
    (state: FreshpaintState) => state.environments.accountMetadata.projects,
  );
  const [selectedEnvID, setSelectedEnvID] = useState<string | null>(
    globalEnvironment?.ID ?? null,
  );

  // Seed from the global current environment once it loads, without overriding
  // a selection the user has already made in the settings picker.
  useEffect(() => {
    if (selectedEnvID == null && globalEnvironment?.ID) {
      setSelectedEnvID(globalEnvironment.ID);
    }
  }, [globalEnvironment?.ID, selectedEnvID]);

  const selectedEnvironment = useMemo<SettingsEnvironment | null>(() => {
    for (const project of projects) {
      const env = project.environments.find((e) => e.ID === selectedEnvID);
      if (env?.ID) {
        return {
          ID: env.ID,
          name: env.name,
          project: { ID: project.ID, name: project.name },
        };
      }
    }
    if (globalEnvironment?.ID) {
      return {
        ID: globalEnvironment.ID,
        name: globalEnvironment.name,
        project: {
          ID: globalEnvironment.project?.ID ?? null,
          name: globalEnvironment.project?.name ?? "",
        },
      };
    }
    return null;
  }, [projects, selectedEnvID, globalEnvironment]);

  const value = useMemo(
    () => ({ selectedEnvironment, selectedEnvID, setSelectedEnvID }),
    [selectedEnvironment, selectedEnvID],
  );

  return (
    <SettingsEnvironmentContext.Provider value={value}>
      {children}
    </SettingsEnvironmentContext.Provider>
  );
};
